import type { TechId } from '@sd/content';
import { builderBot, savingForSwarm, swarmStock } from './builder';
import { contractorBot } from './contractor';
import { makeDiplomat } from './diplomat';
import { combineBots, pausingBot, type Bot } from './match';
import { makeGuard, makeWarlord, prey, rebelBot, responderBot } from './military';
import { makePatron } from './patron';
import { BALANCED, makePlanner } from './planner';
import { makeResearcher } from './researcher';
import { findPickerBot, scoutOnly } from './scout';
import { makeSettler, settlerBot } from './settler';
import { makeStatesman } from './statesman';
import { makeMind, type BotLevel, type Focus, type Module } from './mind';
import { STRATEGY_STYLES } from './strategies';

/**
 * Phase 2 personalities (#31, GDD §10): each plays the whole match and goes
 * for its own victory, answers a rival's countdown, and gives to joint
 * projects in its own way — from the generous to the free rider.
 */
export const PERSONALITY_IDS = ['trader', 'raider', 'scientist', 'cooperator'] as const;
export type PersonalityId = (typeof PERSONALITY_IDS)[number];

const TRADE_FIRST: readonly TechId[] = [
  'tradeProtocols',
  'financialEngineering',
  'appliedScience',
  'automation',
  'agrogenetics',
  'deepMining',
  'scannerMk2',
  'interstellarFinance',
  'fusionReactors',
  'orbitalInfrastructure',
  'xenomaterials',
  'defenseSystems',
  'stellarMeteorology',
  'fleetLogistics',
  'nanofactories',
  'scannerMk3',
  'starControl',
  'magneticScreens',
  'heavyHulls',
  'orbitalAssault',
];

const WAR_FIRST: readonly TechId[] = [
  'defenseSystems',
  'orbitalAssault',
  'fleetLogistics',
  'automation',
  'heavyHulls',
  'agrogenetics',
  'deepMining',
  'appliedScience',
  'tradeProtocols',
  'scannerMk2',
  'fusionReactors',
  'orbitalInfrastructure',
  'financialEngineering',
  'xenomaterials',
  'stellarMeteorology',
  'magneticScreens',
  'nanofactories',
  'interstellarFinance',
  'scannerMk3',
  'starControl',
];

/** Straight for «Управление звездой»: scanners and finance are the two cheapest tier-3 roads. */
const SCIENCE_FIRST: readonly TechId[] = [
  'appliedScience',
  'scannerMk2',
  'tradeProtocols',
  'xenomaterials',
  'financialEngineering',
  'scannerMk3',
  'interstellarFinance',
  'starControl',
  'automation',
  'agrogenetics',
  'deepMining',
  'fusionReactors',
  'orbitalInfrastructure',
  'defenseSystems',
  'fleetLogistics',
  'stellarMeteorology',
  'magneticScreens',
  'nanofactories',
  'heavyHulls',
  'orbitalAssault',
];

/** Crises first, then the finance that lets it gather a coalition of sanctions. */
const SHIELD_FIRST: readonly TechId[] = [
  'stellarMeteorology',
  'appliedScience',
  'tradeProtocols',
  'financialEngineering',
  'interstellarFinance',
  'magneticScreens',
  'agrogenetics',
  'automation',
  'defenseSystems',
  'deepMining',
  'scannerMk2',
  'fusionReactors',
  'orbitalInfrastructure',
  'xenomaterials',
  'fleetLogistics',
  'nanofactories',
  'scannerMk3',
  'starControl',
  'heavyHulls',
  'orbitalAssault',
];

const SETTLER = settlerBot;

/** Modules every personality runs, each on its tab (#148). */
const common = (...modules: Module[]): Module[] => [
  { tab: 'always', bot: pausingBot },
  // A find is a card over the screen: noticed in a few seconds whatever the tab.
  { tab: 'always', bot: findPickerBot },
  ...modules,
  { tab: 'fleet', bot: scoutOnly },
  { tab: 'market', bot: contractorBot },
  { tab: 'always', bot: rebelBot },
];

/** Each personality as its parts: what it does and in which tab a person would do it. */
export const PERSONALITY_MODULES: Readonly<Record<PersonalityId, readonly Module[]>> = {
  /** Economic victory: exports, the reserve currency, levers; gives just enough to be covered. */
  trader: common(
    { tab: 'planet', bot: makePlanner({ ...STRATEGY_STYLES.trader, emergencySell: true }) },
    { tab: 'research', bot: makeResearcher(TRADE_FIRST) },
    { tab: 'market', bot: makeStatesman(true) },
    { tab: 'diplomacy', bot: makeDiplomat({ warlike: false, trader: true }) },
    { tab: 'projects', bot: makePatron({ share: 1, scienceFirst: false }) },
    { tab: 'fleet', bot: makeGuard(2) },
    { tab: 'fleet', bot: responderBot },
    { tab: 'fleet', bot: SETTLER },
  ),
  /** Domination: a war fleet, landings and sieges; a free rider in every project. */
  raider: common(
    {
      tab: 'planet',
      bot: makePlanner({ ...BALANCED, priority: ['researchCenter'], emergencySell: true }),
    },
    { tab: 'research', bot: makeResearcher(WAR_FIRST) },
    { tab: 'fleet', bot: makeWarlord({ warships: 3 }) },
    { tab: 'market', bot: makeStatesman(false) },
    { tab: 'diplomacy', bot: makeDiplomat({ warlike: true, trader: false, target: prey }) },
    { tab: 'projects', bot: makePatron({ share: 0, scienceFirst: false }) },
    { tab: 'fleet', bot: makeGuard(2) },
    { tab: 'fleet', bot: responderBot },
    { tab: 'fleet', bot: makeSettler('exotics') },
  ),
  /** Technological victory: an early research center, the shortest road to the swarm. */
  scientist: common(
    { tab: 'fleet', bot: makeSettler('exotics') },
    {
      tab: 'planet',
      bot: makePlanner({
        ...BALANCED,
        priority: ['researchCenter'],
        hold: savingForSwarm,
        keep: swarmStock,
        emergencySell: true,
      }),
    },
    {
      tab: 'research',
      bot: makeResearcher(SCIENCE_FIRST, { exoticsKept: 0, treasuryMultiple: 1.5, focus: 8 }),
    },
    { tab: 'fleet', bot: builderBot },
    { tab: 'market', bot: makeStatesman(false) },
    { tab: 'diplomacy', bot: makeDiplomat({ warlike: false, trader: false }) },
    { tab: 'projects', bot: makePatron({ share: 1.5, scienceFirst: true, scienceOnly: true }) },
    { tab: 'fleet', bot: makeGuard(2) },
    { tab: 'fleet', bot: responderBot },
  ),
  /** Joint victory: gives more than its part, prepares for crises, joins coalitions. */
  cooperator: common(
    {
      tab: 'planet',
      bot: makePlanner({ ...BALANCED, priority: ['researchCenter'], emergencySell: true }),
    },
    { tab: 'research', bot: makeResearcher(SHIELD_FIRST, { exoticsKept: 0, treasuryMultiple: 2 }) },
    { tab: 'market', bot: makeStatesman(false) },
    { tab: 'diplomacy', bot: makeDiplomat({ warlike: false, trader: false }) },
    { tab: 'projects', bot: makePatron({ share: 2, scienceFirst: false }) },
    { tab: 'fleet', bot: makeGuard(2) },
    { tab: 'fleet', bot: responderBot },
    { tab: 'fleet', bot: SETTLER },
  ),
};

/**
 * The personalities with every tab in view and no delay: instant, stateless.
 * Kept for tests of their decisions; matches use `createBot`.
 */
export const PERSONALITIES: Readonly<Record<PersonalityId, Bot>> = Object.fromEntries(
  PERSONALITY_IDS.map((id) => [id, combineBots(...PERSONALITY_MODULES[id].map((m) => m.bot))]),
) as Record<PersonalityId, Bot>;

/** Where each personality looks most, as a person playing for its victory would (#148). */
const FOCUS: Readonly<Record<PersonalityId, Focus>> = {
  trader: { market: 1.5, planet: 1.2 },
  raider: { fleet: 2.5 },
  scientist: { research: 2, fleet: 1.5 },
  cooperator: { projects: 1.5 },
};

/** A personality with a person's attention (#148): one per seat and match. */
export const createBot = (id: PersonalityId, level: BotLevel = 'normal'): Bot =>
  makeMind(PERSONALITY_MODULES[id], level, FOCUS[id]);
